import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  LayoutDashboard,
  Calendar,
  Stethoscope,
  Settings,
  Users,
  X,
  LogOut,
  Bell,
  Wallet,
  Receipt,
} from "lucide-react";
import AppointmentsSection from "./AppointmentSection";
import DoctorSection from "./DoctorSection";
import AdminSettings from "./AdminSettings";
import RemindersSection from "./ReminderSection";
import JobManagement from "./JobManagement";
import Dashboard from "./Dashboard";
import Header from "../Header";
import { collection, getDocs, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase";
import AdminTodayQueue from "./AdminTodayQueue";
import PatientPage from "./PatientPage"; // ✅ make sure path is correct
import PaymentsSection from "./PaymentsSection";
import ExpensesSection from "./ExpensesSection";
const navItems = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "tokens", label: "Tokens", icon: Bell },
  { key: "doctors", label: "Doctors", icon: Stethoscope },
  { key: "patients", label: "Patients", icon: Users },
  { key: "reminders", label: "Reminders", icon: Bell },
  { key: "jobManagement", label: "Job Management", icon: LayoutDashboard },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "expenses", label: "Expenses", icon: Receipt },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(window.innerWidth >= 768); // open on desktop, collapsed on mobile
  const [activeTab, setActiveTab] = useState("dashboard");
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [quickConsultations, setQuickConsultations] = useState([]);
  const [tokensData, setTokensData] = useState([]);
  const [patients, setPatients] = useState([]); // 🔥 FIX
  const [treatments, setTreatments] = useState([]);
  const [packages, setPackages] = useState([]); // ✅ ADD THIS
  const [packagePayments, setPackagePayments] = useState([]);
  const [callbackRequests, setCallbackRequests] = useState([]);
  const [admin, setAdmin] = useState({ name: "", role: "Admin" });
  const [selectedVPJC, setSelectedVPJC] = useState(null);
  const [searchParams] = useSearchParams();

  const tabFromUrl = searchParams.get("tab");
  const vpjcFromUrl = searchParams.get("vpjcId");
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const snap = await getDocs(collection(db, "patients"));
        const list = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPatients(list);
      } catch (error) {
        console.error("Error fetching patients:", error);
      }
    };

    fetchPatients();
  }, []);

  // 1️⃣ Load admin from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      setAdmin({ name: parsed.name || "Admin", role: parsed.role || "Admin" });
    }
  }, []);

  // 2️⃣ Read tab from URL
  useEffect(() => {
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // 3️⃣ Read VPJC from URL
  useEffect(() => {
    if (vpjcFromUrl) {
      setSelectedVPJC(vpjcFromUrl);
      setActiveTab("patients");
    } else {
      // 🔥 VERY IMPORTANT
      setSelectedVPJC(null);
    }
  }, [vpjcFromUrl]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setIsOpen(false);
      else setIsOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // optional: update on resize

  const fetchAppointments = async () => {
    try {
      // ----------------------
      // Appointments
      // ----------------------
      const snapshot = await getDocs(collection(db, "appointments"));
      const appointmentsData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          bookingId: data.bookingId || doc.id,
          name: data.name || "N/A",
          email: data.email || "N/A",
          phone: data.phone || "N/A",
          date: data.date || "N/A",
          time: data.time || "N/A",
          service: data.service || "N/A",
          status: data.status || "Pending",
          consultationType: data.consultationType || "Walk-in",
          branch: data.branch || "",
          pincode: data.pincode || "",
          locality: data.locality || "",
          flatNo: data.flatNo || "",
          landmark: data.landmark || "",
          message: data.message || "N/A",
          cancelReason: data.cancelReason || "",
          createdAt: data.timestamp ? data.timestamp.toDate() : new Date(0),
        };
      });

      appointmentsData.sort((a, b) => b.createdAt - a.createdAt);
      setAppointments(appointmentsData);

      // ----------------------
      // Quick Consultations
      // ----------------------
      const quickSnap = await getDocs(collection(db, "quickConsultations"));
      const quickData = quickSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().timestamp
          ? doc.data().timestamp.toDate()
          : new Date(0),
      }));
      quickData.sort((a, b) => b.createdAt - a.createdAt);
      setQuickConsultations(quickData);

      // ----------------------
      // Callback Requests
      // ----------------------
      const cbSnap = await getDocs(collection(db, "callbackRequests"));
      const cbData = cbSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().timestamp
          ? doc.data().timestamp.toDate()
          : new Date(0),
      }));
      cbData.sort((a, b) => b.createdAt - a.createdAt);
      setCallbackRequests(cbData);

    } catch (err) {
      console.error("Error fetching dashboard data:", err);
    }
  };
  // 4️⃣ Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) setIsOpen(false);
      else setIsOpen(true);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
  const unsub = onSnapshot(
    collection(db, "visits"),
    (snapshot) => {
      const visitsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        visit_date: doc.data().visit_date || "",
        consultation_payment_status:
        doc.data().consultation_payment_status || "UNPAID",
        consultation_fee: doc.data().consultation_fee || 0,
        created_at: doc.data().created_at || null,
      }));

      console.log("Realtime Visits:", visitsData.length);
      setTokensData(visitsData);
    }
  );

  return () => unsub();
}, []);

useEffect(() => {
  const unsub = onSnapshot(
    collection(db, "treatment_entries"),
    (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setTreatments(data);
    }
  );

  return () => unsub();
}, []);

useEffect(() => {
  const unsub = onSnapshot(
    collection(db, "package_payments"),
    (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPackagePayments(data);
    }
  );

  return () => unsub();
}, []);

useEffect(() => {
  const unsub = onSnapshot(
    collection(db, "packages"),
    (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPackages(data);
    }
  );

  return () => unsub();
}, []);
  const getInitials = (name) =>
    name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "A";

  return (
    <>
      <Header hideBookButton={true} />

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 h-[calc(100%-4rem)] bg-white border-r shadow-sm flex flex-col justify-between transition-all duration-300 z-40
          ${isOpen ? "w-64" : "w-16"} md:${isOpen ? "w-64" : "w-20"}`}
      >
        {/* Toggle */}
        {/* Top section: X + Admin info in same section */}
        <div className="flex flex-col items-center py-2 border-b">
          {/* X button on top-right */}
          {isOpen && (
            <button
              onClick={() => setIsOpen(false)}
              className="self-end mr-2 text-blue-700 hover:text-blue-900"
            >
              <X size={20} />
            </button>
          )}

          {/* Admin Avatar and Name */}
          {isOpen && (
            <div className="flex flex-col items-center mt-1">
              <div className="h-16 w-16 rounded-full bg-blue-200 text-blue-700 flex items-center justify-center font-bold text-xl mb-1 border-2 border-blue-600">
                {getInitials(admin.name)}
              </div>
              <h2 className="text-lg font-semibold text-blue-700">
                {admin.name}
              </h2>
              <p className="text-sm text-gray-500">
                {admin.role?.charAt(0).toUpperCase() + admin.role?.slice(1)}
              </p>
            </div>
          )}

          {/* Hamburger centered when collapsed */}
          {!isOpen && (
            <button
              onClick={() => setIsOpen(true)}
              className="flex items-center justify-center w-10 h-10 my-2 rounded hover:bg-gray-100"
            >
              <div className="flex flex-col justify-center items-center h-4 w-6 gap-1">
                <span className="block h-0.5 w-full bg-blue-700 rounded"></span>
                <span className="block h-0.5 w-full bg-blue-700 rounded"></span>
                <span className="block h-0.5 w-full bg-blue-700 rounded"></span>
              </div>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col justify-between p-2 sm:p-3 overflow-y-auto">
          <div className="flex flex-col gap-2">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={() => {
                  if (item.key === "patients") {
                    navigate(`/admin-dashboard?tab=patients`); // ✅ NO vpjcId
                    setSelectedVPJC(null); // 🔥 CLEAR OLD
                  } else {
                    navigate(`/admin-dashboard?tab=${item.key}`);
                  }
                }}
                className={`flex items-center gap-3 w-full px-2 py-2 rounded-xl text-sm transition-colors
                  ${
                    activeTab === item.key
                      ? "bg-blue-100 text-blue-700"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
              >
                <item.icon size={20} />
                {isOpen && <span>{item.label}</span>}
              </button>
            ))}
          </div>

          {/* Logout */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="flex items-center gap-3 w-full px-2 py-2 rounded-xl text-sm text-red-600 hover:bg-red-100 mt-4"
          >
            <LogOut size={20} />
            {isOpen && <span>Logout</span>}
          </button>
        </nav>

        {/* Logout Dialog */}
        <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm Logout</DialogTitle>
              <DialogDescription>
                Are you sure you want to log out?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex justify-end space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowLogoutConfirm(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() => {
                  localStorage.removeItem("user");
                  auth.signOut();
                  navigate("/login");
                }}
              >
                Logout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </aside>

      {/* Main content */}
      <main
        className={`pt-24 px-2 sm:px-6 lg:px-8 transition-all
          ${isOpen ? "ml-16 sm:ml-64 md:ml-64" : "ml-16 sm:ml-20 md:ml-20"}`}
      >
        {activeTab === "dashboard" && (
          <Dashboard
            appointments={appointments}
            tokens={tokensData}
            patients={patients}
            treatments={treatments}
            packagePayments={packagePayments}
            packages={packages}
          />
        )}
        {activeTab === "appointments" && (
          <AppointmentsSection
            appointments={appointments}
            quickConsultations={quickConsultations}
            callbackRequests={callbackRequests}
            fetchAppointments={fetchAppointments}
          />
        )}
        {activeTab === "tokens" && (
          <AdminTodayQueue
            onSelectPatient={(vpjcId) => {
              navigate(`/admin-dashboard?tab=patients&vpjcId=${vpjcId}`);
            }}
          />
        )}
        {activeTab === "doctors" && <DoctorSection />}
        {activeTab === "patients" && (
          <PatientPage
            key={selectedVPJC || "empty"} // 🔥 force remount
            searchVPJC={selectedVPJC}
          />
        )}
        {activeTab === "reminders" && <RemindersSection db={db} />}
        {activeTab === "jobManagement" && <JobManagement />}
        {activeTab === "payments" && <PaymentsSection />}
        {activeTab === "expenses" && <ExpensesSection />}
        {activeTab === "settings" && <AdminSettings />}
      </main>
    </>
  );
}
